
import {
  Box, Button, Dialog, DialogContent, MenuItem, TextField, Typography,
} from "@mui/material"
import { BoxFC, BoxFR } from "components/BoxCustom"
import DataGridCellExpand from "components/DataGridCellExpand/DataGridCellExpand"
import ComboBox from "components/ComboBox"
import PaperComponent from "components/PaperComponent"
import { DialogFooter, DialogHeader } from "components/dialog/DialogHeader"
import { GlobalStateContext } from "contexts/GlobalStateContext"
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { DesktopDatePicker } from "@mui/x-date-pickers-pro"
import { alertConfirmDelete } from "components/Alert"
import { bahtProps } from "utils/bahtProps"
import { numberFormat, selectOnFocus } from "utils"
import { AddRounded, AutoAwesomeRounded } from "resource/muiImport"
import FilePreviewCard from "components/FileUplaod/FilePreviewCard"
import dayjs from "dayjs"
import { initTruckFixCostDialogData } from "./initData"
import { truckFixCostColNm } from "./constant"
import { truckFixCostItemColumns } from "./columns"
import { addSumRow, getSumColorClassName, sxSumColorClass } from "utils/highlightSumColor"
import ReadTruckFixCostFileDialog from "./ReadTruckFixCostFileDialog"
import UploadProgressDialog from "./UploadProgressDialog"
import { truckFixCostPacer } from "./pacer"

/**
 * The number of months a document covers, inclusive — the same count AMORT_SQL's 'M'
 * branch uses. PERIOD_DIFF works on the year-month alone, so dayjs's diff('month') is the
 * wrong tool: it needs a FULL month and would call 31/01 -> 01/02 zero months where the
 * report calls it two, and the screen must never disagree with the report.
 */
const monthsInPeriod = (strDte, endDte) => {
  if (!strDte || !endDte) return 0
  const st = dayjs(strDte), en = dayjs(endDte)
  if (!st.isValid() || !en.isValid() || en.isBefore(st, 'day')) return 0
  return (en.year() - st.year()) * 12 + (en.month() - st.month()) + 1
}

/**
 * One ค่าใช้จ่ายประจำรถ document and the vehicles it covers.
 *
 * @param selectedId  TukFixCostId to edit, or null/0 to add
 * @param presetData  seeds a NEW document (Menu 2 opens this already pointed at a truck
 *                    and document type)
 * @param typCombo    optional; when the caller has not loaded the types the dialog
 *                    fetches them itself, so it can be dropped into any page
 */
const TruckFixCostDialog = ({ selectedId, dialogOpen, setDialogOpen, onFinish
  , typCombo: typComboProp, presetData }) => {

  const { ax, msData } = useContext(GlobalStateContext)
  const [dialogData, setDialogData] = useState({ ...initTruckFixCostDialogData })
  const [ownTypCombo, setOwnTypCombo] = useState([])
  const [filesData, setFilesData] = useState([])
  const [pendingFiles, setPendingFiles] = useState([])
  const [readDialogOpen, setReadDialogOpen] = useState(false)
  // { done, total, waitSec } while บันทึก is running, null when idle
  const [progress, setProgress] = useState(null)
  // The footer button has no disabled prop and state lands a render late — this is what
  // actually stops a second บันทึก during the minute the pacer may be waiting.
  const savingRef = useRef(false)

  const typCombo = typComboProp && typComboProp.length ? typComboProp : ownTypCombo
  // memoised: three hooks depend on it, and a fresh [] each render re-ran all of them
  const items = useMemo(() => dialogData.Items || [], [dialogData.Items])

  const dialogDataProp = useCallback((name) => ({
    fullWidth: true,
    size: "small",
    label: truckFixCostColNm[name],
    value: dialogData[name] ?? "",
    onChange: (e) => setDialogData(o => ({ ...o, [name]: e.target.value })),
  }), [dialogData])

  const datePickerProp = useCallback((name) => ({
    label: truckFixCostColNm[name],
    inputFormat: "DD/MM/YYYY",
    value: dialogData[name] ?? null,
    onChange: (v) => setDialogData(o => ({ ...o, [name]: v ? dayjs(v).format("YYYY-MM-DD") : null })),
    renderInput: (params) => <TextField size="small" fullWidth {...params} />,
  }), [dialogData])

  const itemSum = useMemo(() => items.reduce((s, it) => s + (+it.Amnt || 0), 0), [items])
  const sumMatches = Math.abs(itemSum - (+dialogData.Amnt || 0)) < 0.005

  // Same maths the report runs, shown live — a wrong period or a missing NumPrd is obvious
  // at entry rather than three months later in TruckIncomeMonthly.
  const months = useMemo(() => monthsInPeriod(dialogData.StrDte, dialogData.EndDte)
    , [dialogData.StrDte, dialogData.EndDte])

  // const monthlyPreview = useMemo(() => (months && itemSum) ? itemSum / months : null
  //   , [months, itemSum])

  const handleTypChange = useCallback((typId) => {
    setDialogData(o => ({ ...o, TukFixCostTypId: typId }))
  }, [])

  const addItem = useCallback((key, id) => {
    if (!id) return
    setDialogData(o => {
      if ((o.Items || []).some(it => it[key] === id)) return o   // already covered
      return { ...o, Items: [...(o.Items || []), { [key]: id, Amnt: 0, DocNo: "", Files: [] }] }
    })
  }, [])

  const setItemField = useCallback((idx, key, value) => {
    setDialogData(o => {
      const next = [...o.Items]
      next[idx] = { ...next[idx], [key]: value }
      return { ...o, Items: next }
    })
  }, [])

  const removeItem = useCallback((idx) => {
    setDialogData(o => ({ ...o, Items: o.Items.filter((_, i) => i !== idx) }))
  }, [])

  /**
   * Split the header total across the vehicles. The remainder lands on the LAST line so
   * the items always add back to the control total exactly — ฿100,000 / 42 is
   * 2,380.95 × 41 + 2,380.97, not 42 recurring decimals. Amounts stay editable
   * afterwards, because a fleet premium is rarely actually even.
   */
  const splitEvenly = useCallback(() => {
    setDialogData(o => {
      const n = (o.Items || []).length
      if (!n) return o
      const total = Math.round((+o.Amnt || 0) * 100)
      const each = Math.floor(total / n)
      const next = o.Items.map((it, i) => ({
        ...it,
        Amnt: ((i === n - 1 ? total - each * (n - 1) : each) / 100).toFixed(2),
      }))
      return { ...o, Items: next }
    })
  }, [])

  /**
   * ใช้ข้อมูลนี้ from ReadTruckFixCostFileDialog — turn the drafts read off the paper into
   * this document.
   *
   * The HEADER comes from the first draft (all the files of one batch are the same
   * ประเภท/period in practice, and every field stays editable). The TOTAL is the sum of
   * what was read per vehicle whenever more than one file was dropped, because 26
   * ป้ายวงกลม paid together are one receipt whose total is their sum — a single file
   * keeps its own printed total instead.
   *
   * Each file rides along on its vehicle line as _pendingFile and is uploaded as that
   * vehicle's certificate after the document is saved (see handleDialogOk) — the
   * per-vehicle file needs a TukFixCostItmId, which does not exist until then.
   */
  const handleApplyDrafts = useCallback((drafts) => {
    if (!drafts || drafts.length === 0) return
    const hdr = drafts[0]
    setDialogData(o => {
      const items = [...(o.Items || [])]
      for (const d of drafts) {
        for (const it of (d.Items || [])) {
          const at = items.findIndex(oi => (it.TukId && oi.TukId === it.TukId)
            || (it.TlrId && oi.TlrId === it.TlrId))
          // A vehicle already on the document keeps its line and only gains what it was
          // missing — re-reading the same paper must not create a second line for it.
          if (at >= 0) {
            items[at] = {
              ...items[at],
              DocNo: items[at].DocNo || it.DocNo,
              Amnt: +items[at].Amnt ? items[at].Amnt : it.Amnt,
              _pendingFile: items[at]._pendingFile || d.file,
            }
          } else {
            items.push({ ...it, Files: [], _pendingFile: d.file })
          }
        }
      }
      const itmSum = items.reduce((s, it) => s + (+it.Amnt || 0), 0)
      return {
        ...o,
        TukFixCostTypId: hdr.TukFixCostTypId ?? o.TukFixCostTypId,
        StrDte: hdr.StrDte || o.StrDte,
        EndDte: hdr.EndDte || o.EndDte,
        PayDte: hdr.PayDte || o.PayDte,
        DocNo: hdr.DocNo || o.DocNo,
        SupId: hdr.SupId ?? o.SupId,
        Amnt: drafts.length > 1 ? itmSum : (hdr.Amnt || o.Amnt),
        Items: items,
      }
    })
  }, [])

  /**
   * Attach each read file to the vehicle line it came from, once the ids exist.
   *
   * One request per vehicle, so 26 ป้ายวงกลม is 26 requests on a path that allows 30 a
   * minute — every one of them goes through the pacer, which parks the batch until the
   * window rolls over rather than letting the tail of it 429 away.
   */
  const uploadItemFiles = useCallback(async (tukFixCostId, onWait, onDone) => {
    const pending = items.filter(it => it._pendingFile)
    if (pending.length === 0) return
    await truckFixCostPacer.take(onWait)
    const value = await ax.post("/truck-fix-cost/getTruckFixCostDetail", { TukFixCostId: tukFixCostId })
    const saved = value.data?.[0]?.Items || []
    for (const it of pending) {
      const row = saved.find(s => (it.TukId && s.TukId === it.TukId)
        || (it.TlrId && s.TlrId === it.TlrId))
      if (!row) continue
      await truckFixCostPacer.take(onWait)
      const formData = new FormData()
      formData.append('file', it._pendingFile)
      formData.append('TukFixCostItmId', row.TukFixCostItmId)
      formData.append('Label', it._pendingFile.name)
      await ax.post("/truck-fix-cost/uploadTruckFixCostItemFile", formData)
      if (onDone) onDone()
    }
  }, [ax, items])

  /**
   * Grid rows for the vehicles, plus the รวม row that addSumRow appends as id 0 and the
   * grid pins to the bottom.
   *
   * Ids start at 1 BECAUSE of that: a row is addressed by its index in `items` (a line
   * added from a combo, or several added at once by the AI read, has no TukFixCostItmId
   * yet), and index 0 would collide with the sum row. So id = index + 1 throughout, and
   * every handler subtracts it back.
   */
  const itemRows = useMemo(() => addSumRow({
    dataTable: items.map((it, idx) => ({
      ...it,
      id: idx + 1,
      MonthAmnt: months ? (+it.Amnt || 0) / months : null,
    })),
    sumColNms: ["Amnt", "MonthAmnt"],
    sumColTotal: "Veh",
  }), [items, months])

  /** Pull the saved items back so a row shows the file that was just attached/removed. */
  const reloadItems = useCallback(() => {
    if (!selectedId) return
    ax.post("/truck-fix-cost/getTruckFixCostDetail", { TukFixCostId: selectedId }).then(value => {
      if (value.data && value.data.length) {
        setDialogData(o => ({ ...o, Items: value.data[0].Items || [] }))
      }
    })
  }, [ax, selectedId])

  const handleDeleteItemFile = useCallback((file) => {
    alertConfirmDelete(() => {
      ax.post("/truck-fix-cost/deleteTruckFixCostItemFile",
        { TukFixCostItmFileId: file.TukFixCostItmFileId }).then(value => {
        if (value.data) reloadItems()
      })
    })
  }, [ax, reloadItems])

  const itemColumns = useMemo(() => truckFixCostItemColumns({
    truckCombo: msData.truckCombo,
    trailerCombo: msData.trailerCombo,
    onPickTruck: (id, tukId) => setItemField(id - 1, 'TukId', tukId),
    onDelete: (id) => removeItem(id - 1),
    handleDeleteFile: handleDeleteItemFile,
    onFileUploadSuccess: reloadItems,
    // A line with no TukFixCostItmId yet keeps its file here; uploadItemFiles sends it
    // once บันทึก has created the row.
    onDeferFile: ({ row }, file) => setItemField(row.id - 1, '_pendingFile', file),
  }), [msData.truckCombo, msData.trailerCombo, setItemField, removeItem
    , handleDeleteItemFile, reloadItems])

  const handleItemEditCommit = useCallback((params) => {
    if (params.id === 0 || !['DocNo', 'Amnt'].includes(params.field)) return
    setItemField(params.id - 1, params.field, params.value)
  }, [setItemField])

  const uploadHdrFiles = useCallback(async (tukFixCostId, onWait, onDone) => {
    for (const pf of pendingFiles) {
      await truckFixCostPacer.take(onWait)
      const formData = new FormData()
      formData.append('file', pf.file)
      formData.append('TukFixCostId', tukFixCostId)
      formData.append('Label', pf.name)
      await ax.post("/truck-fix-cost/uploadTruckFixCostFile", formData)
      if (onDone) onDone()
    }
    setPendingFiles([])
  }, [ax, pendingFiles])

  /**
   * บันทึก: save the document, then send every file it is holding.
   *
   * The uploads are one request per file and the path allows 30 a minute, so the whole
   * run is paced — past the twentieth request it stops and waits out the window instead
   * of losing the rest to a 429. UploadProgressDialog says so, because a save that pauses
   * for a minute with no explanation looks like a hang.
   */
  const handleDialogOk = useCallback(async () => {
    if (!items.length || savingRef.current) return
    const url = selectedId
      ? "/truck-fix-cost/updateTruckFixCost"
      : "/truck-fix-cost/insertTruckFixCost"
    const total = pendingFiles.length + items.filter(it => it._pendingFile).length
    const onWait = (waitSec) => setProgress(o => o && ({ ...o, waitSec }))
    const onDone = () => setProgress(o => o && ({ ...o, done: o.done + 1, waitSec: 0 }))
    savingRef.current = true
    setProgress({ done: 0, total, waitSec: 0 })
    try {
      await truckFixCostPacer.take(onWait)
      // _pendingFile / LicNm / Matched are client-side only — a File does not survive JSON
      // and the rest are not columns.
      const value = await ax.post(url, {
        ...dialogData,
        Items: items.map(({ _pendingFile, LicNm, Matched, Files, ...it }) => it),
      })
      if (!value.data) return
      const id = selectedId || value.data
      if (pendingFiles.length > 0) await uploadHdrFiles(id, onWait, onDone)
      await uploadItemFiles(id, onWait, onDone)
      // The list behind the dialog reloads on the same path — budget the pacer never sees.
      truckFixCostPacer.spend()
      onFinish()
      setDialogOpen(false)
    } finally {
      savingRef.current = false
      setProgress(null)
    }
  }, [ax, dialogData, items, onFinish, setDialogOpen, selectedId, pendingFiles, uploadHdrFiles
    , uploadItemFiles])

  const handleDelete = useCallback(() => {
    alertConfirmDelete(() => {
      ax.post("/truck-fix-cost/deleteTruckFixCost", { TukFixCostId: selectedId }).then(value => {
        if (value.data) {
          onFinish()
          setDialogOpen(false)
        }
      })
    })
  }, [ax, onFinish, selectedId, setDialogOpen])

  const handleDeleteServerFile = useCallback((file) => new Promise((resolve) => {
    alertConfirmDelete(() => {
      ax.post("/truck-fix-cost/deleteTruckFixCostFile",
        { TukFixCostFileId: file.TukFixCostFileId }).then(v => { if (v.data) resolve() })
    })
  }), [ax])

  useEffect(() => {
    if (!dialogOpen) {
      setDialogData({ ...initTruckFixCostDialogData })
      setFilesData([])
      setPendingFiles(o => { o.forEach(f => URL.revokeObjectURL(f.preview)); return [] })
      return
    }
    if (!typComboProp || !typComboProp.length) {
      ax.post("/truck-fix-cost/getTruckFixCostType", { IsActv: 1 }).then(value => {
        if (value.data) {
          setOwnTypCombo(value.data.map(t => ({
            id: t.TukFixCostTypId, label: t.TukFixCostTypNm,
          })))
        }
      })
    }
    if (selectedId) {
      ax.post("/truck-fix-cost/getTruckFixCostDetail", { TukFixCostId: selectedId }).then(value => {
        if (value.data && value.data.length > 0) {
          setDialogData({ ...value.data[0], Items: value.data[0].Items || [] })
          setFilesData(value.data[0].Files || [])
        }
      })
    } else {
      // presetData wins over the defaults but never over a loaded document — opening an
      // existing policy must show that policy, not the cell you happened to click.
      const preset = presetData || {}
      setDialogData({
        ...initTruckFixCostDialogData,
        ...(preset.TukFixCostTypId ? { TukFixCostTypId: preset.TukFixCostTypId } : {}),
        Items: preset.TukId ? [{ TukId: preset.TukId, Amnt: 0, Files: [] }] : [],
      })
      setFilesData([])
    }
    // presetData is a fresh object each render; depending on it would refetch in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen, ax, selectedId, typComboProp])

  return (
    <Dialog
      open={dialogOpen}
      onClose={(e, reason) => { if (reason !== "backdropClick") setDialogOpen(false) }}
      PaperComponent={PaperComponent}
      aria-labelledby="draggable-dialog-title"
      fullWidth maxWidth='md'
    >
      <DialogHeader handleDialogClose={() => setDialogOpen(false)}
        title="ค่าใช้จ่ายประจำรถ"
        onDeleteClick={selectedId ? handleDelete : null}
      />
      <DialogContent>
        <BoxFC pt={2} sx={{ gap: 1 }}>
          {/* Read the paper first, then correct it — every field below stays editable. */}
          <BoxFR sx={{ gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              อ่านเอกสารด้วย AI แล้วกรอกให้อัตโนมัติ (ตรวจสอบก่อนบันทึกเสมอ)
            </Typography>
            <BoxFR sx={{ flex: 1 }} />
            <Button size="small" variant="outlined" startIcon={<AutoAwesomeRounded />}
              onClick={() => setReadDialogOpen(true)}>
              อ่านจากไฟล์
            </Button>
          </BoxFR>

          <BoxFR sx={{ gap: 1 }}>
            <TextField {...dialogDataProp('TukFixCostTypId')} select required
              onChange={(e) => handleTypChange(e.target.value)}>
              {typCombo.map(t => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
            </TextField>
            <DesktopDatePicker {...datePickerProp('StrDte')} />
            <DesktopDatePicker {...datePickerProp('EndDte')} />
            <DesktopDatePicker {...datePickerProp('PayDte')} />
          </BoxFR>

          <BoxFR sx={{ gap: 1 }}>
            <ComboBox
              sx={{ flex: 2 }}
              options={msData.supplierCombo || []}
              label="ผู้ขาย/ประกัน"
              selectedId={dialogData.SupId}
              setSelectedId={(id) => setDialogData(o => ({ ...o, SupId: id }))}
            />
            <TextField {...dialogDataProp('DocNo')} sx={{ flex: 1}} />
            <TextField {...dialogDataProp('Amnt')} type="number" {...bahtProps} required
              onFocus={selectOnFocus} sx={{ flex: 1 }}
            />
            {/* The only check on the total: ค่าผ่อนงวดรถ is keyed as a whole contract but
                what the operator KNOWS is the installment, so read it back to them. */}
            {/* <TextField size="small" label={truckFixCostColNm.MonthAmnt} disabled
              value={monthlyPreview == null ? "-" : numberFormat(monthlyPreview)}
              sx={{ flex: 1 }}
            /> */}
          </BoxFR>

          {/* ------- vehicles covered ------- */}
          <BoxFR sx={{ gap: 1, mt: 1 }}>
            <Typography variant="subtitle2">รถที่คุ้มครอง ({items.length} คัน)</Typography>
            <BoxFR sx={{ flex: 1 }} />
            <ComboBox
              options={msData.truckCombo || []}
              label="เพิ่มรถ"
              selectedId={null}
              setSelectedId={(id) => addItem('TukId', id)}
              sx={{ width: 160 }}
            />
            <ComboBox
              options={msData.trailerCombo || []}
              label="เพิ่มหางพ่วง"
              selectedId={null}
              setSelectedId={(id) => addItem('TlrId', id)}
              sx={{ width: 160 }}
            />
            <Button size="small" variant="outlined" startIcon={<AddRounded />} onClick={splitEvenly}>
              แบ่งเท่ากัน
            </Button>
          </BoxFR>

          <Box height={300} sx={sxSumColorClass}>
            <DataGridCellExpand
              experimentalFeatures={{ rowPinning: true }}
              hideFooter
              hideToolbar
              disableSelectionOnClick
              rows={itemRows}
              columns={itemColumns}
              onCellEditCommit={handleItemEditCommit}
              isCellEditable={(params) => params.id !== 0}
              pinnedRows={{ bottom: [itemRows.find(row => row.id === 0)] }}
              getRowClassName={getSumColorClassName}
            />
          </Box>

          {!sumMatches && (
            <Typography variant="caption" color="error.main">
              ยอดแยกคันรวม {numberFormat(itemSum)} ไม่ตรงกับยอดตามใบเสร็จ ({numberFormat(+dialogData.Amnt || 0)})
            </Typography>
          )}


          <BoxFR sx={{ gap: 1 }}>
            <TextField {...dialogDataProp('Rmk')} />
            <TextField {...dialogDataProp('IsActv')} select sx={{ width: 140 }}
              onChange={(e) => setDialogData(o => ({ ...o, IsActv: +e.target.value }))}>
              <MenuItem value={1}>ใช้งาน</MenuItem>
              <MenuItem value={0}>ยกเลิก</MenuItem>
            </TextField>
          </BoxFR>
          {/* the ONE policy PDF for the batch — stored once, not once per vehicle */}
          {/* <Typography variant="subtitle2" sx={{ mt: 1 }}>ไฟล์เอกสาร (ทั้งใบ)</Typography> */}
          <FilePreviewCard
            filesData={filesData}
            setFilesData={setFilesData}
            pendingFiles={pendingFiles}
            setPendingFiles={setPendingFiles}
            onDeleteServerFile={handleDeleteServerFile}
            fileIdKey="TukFixCostFileId"
          />
        </BoxFC>
      </DialogContent>
      <DialogFooter okText="บันทึก"
        handleDialogClose={() => setDialogOpen(false)}
        handleDialogOk={handleDialogOk} />

      <ReadTruckFixCostFileDialog
        dialogOpen={readDialogOpen}
        setDialogOpen={setReadDialogOpen}
        onApply={handleApplyDrafts}
      />

      <UploadProgressDialog progress={progress} />

    </Dialog>
  )
}

export default React.memo(TruckFixCostDialog)
